import React, { useEffect, useState } from "react";
import Loader from "../../components/loader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  BIG_FIVE_TEST_ASSESSMENT_ID,
  CONTENT_KEY_BIG_FIVE,
  MODEL_TYPE_LOVE_STYLE,
  PERSONALITY_TEST_ASSESSMENT_ID,
  traitTitleKeyMap,
} from "../../utils/general-config";
import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top/component";
import Header from "../../components/header";
import {
  getCookie,
  getEnneagramBannerImage,
  getLoveStylePageImage,
  snakeToTitleCase,
} from "../../utils/utility-functions";

import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";

import {
  APP_NAVIGATION,
  LOCALE,
  LOVE_STYLE_CONTENT,
} from "../../utils/constants";
import PathHelper from "../../utils/path-helper";
import ExpandableSection from "../../components/expandable-collapsible/component";
import { useTranslation } from "react-i18next";

const LoveStyleReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,
}) => {
  const navigate = useNavigate();

  const [contentData, setContentData] = useState({});
  const { t } = useTranslation();
  const [userTraits, setUserTraits] = useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  const token = getCookie("jwtToken");
  const userId = queryParams.get("uid") || userGUID;
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_LOVE_STYLE[currentLocale] ||
            MODEL_TYPE_LOVE_STYLE[LOCALE.EN],
          contentKey: CONTENT_KEY_BIG_FIVE,
        });
        if (contentResponse) {
          const { content } = contentResponse;
          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId]);

  const result1ImageText = "love-style-report-1";
  const result2ImageText = "love-style-report-2";
  const result3ImageText = "love-style-report-3";
  const result4ImageText = "love-style-report-4";
  const result5ImageText = "love-style-report-5";
  const result6ImageText = "love-style-report-6";
  const result1Image = getLoveStylePageImage(result1ImageText);
  const result2Image = getLoveStylePageImage(result2ImageText);
  const result3Image = getLoveStylePageImage(result3ImageText);
  const result4Image = getLoveStylePageImage(result4ImageText);
  const result5Image = getLoveStylePageImage(result5ImageText);
  const result6Image = getLoveStylePageImage(result6ImageText);
  const bannerImage = getEnneagramBannerImage();
  const yourLoveStyle = contentData?.yourLoveStyle;
  const emotionalConnector = contentData?.emotionalConnector;
  const expressiveCommunicator = contentData?.expressiveCommunicator;
  const independentPartner = contentData?.independentPartner;
  const physicalAffectionateLover = contentData?.physicalAffectionateLover;
  const practicalSupporter = contentData?.practicalSupporter;
  const nextSteps = contentData?.nextSteps;

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
    pink: "from-pink-600 via-pink-500",
  };

  const progressBarColor = [
    "#ff6284",
    "#49A2E6",
    "#805bec",
    "#ffce55",
    "#4cc0c0",
    "#ec6af9",
  ];

  const TakeTestButton = ({ onClick }) => {
    return (
      <button
        className="flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full shadow-md border border-gray-300 hover:bg-gray-100 transition-all"
        onClick={onClick}
      >
        {t("report.career_preference_precontent.take_test_cta")}
        <svg
          fill="black"
          width="20"
          height="20"
          viewBox="-2 -2 24 24"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin"
          className="jam jam-arrow-circle-right"
        >
          <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1.586-7H6a1 1 0 0 1 0-2h5.586L9.05 6.464a1 1 0 1 1 1.414-1.414l4.243 4.243a.997.997 0 0 1 0 1.414l-4.243 4.243a1 1 0 1 1-1.414-1.414L11.586 11z" />
        </svg>
      </button>
    );
  };

  const HeroBanner = ({ title, color = "purple", buttonNeeded, onClick }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    return (
      <div
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600`}
      >
        {/* Background Image (Hidden on small screens) */}
        <img
          src={bannerImage}
          alt={title}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-full object-cover object-right"
        />
        {/* Gradient Overlay */}
        <div
          className={`block absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent`}
        />
        <div className="absolute inset-0 bg-black opacity-10" />

        {/* Hero Content */}
        <div className="relative h-full flex items-center">
          <div className="w-full px-4 py-8 lg:px-8 lg:py-12">
            {/* Title & Button Wrapper */}
            <div className="flex flex-col md:flex-row items-start justify-between gap-4 pr-10 md:pr-16">
              {/* Title */}
              <h2
                style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
                className="text-3xl md:text-4xl font-bold text-white break-words whitespace-pre-wrap text-left"
              >
                {title}
              </h2>

              {/* Button (if needed) */}
              {buttonNeeded && (
                <div className="flex-shrink-0">
                  <TakeTestButton onClick={onClick} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const CallToActionSection = ({ onClick1, onClick2 }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.adhd.testMessage")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.adhd.discoverMessage")}
            </p>
            <TakeTestButton onClick={onClick1} />
          </div>
        </div>
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.adhd.bigFiveMessage")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.adhd.strengthMesssge")}
            </p>
            <TakeTestButton onClick={onClick2} />
          </div>
        </div>
      </section>
    );
  };

  const ProgressBar = ({ label, percentage, color }) => {
    return (
      <li>
        <div className="flex justify-between items-center mb-2">
          <span className="font-semibold text-gray-700">{label}</span>
          <span className="text-sm" style={{ color }}>
            {percentage}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-5">
          <div
            className="h-5 rounded-full transition-all duration-500"
            style={{ width: `${percentage}%`, backgroundColor: color }}
          ></div>
        </div>
      </li>
    );
  };

  const LoveCard = ({
    content,
    bgColor = "bg-amber-50",
    headingColor = "text-amber-800",
  }) => {
    return (
      <div className={`rounded-xl p-4 md:p-6 ${bgColor} shadow-lg w-full`}>
        <div className="space-y-4">
          {content?.Content.map((section, index) => (
            <div key={index}>
              <h3 className={`font-semibold ${headingColor} mb-2`}>
                {section.Heading}
              </h3>
              <p className="text-gray-700">{section.Content}</p>
            </div>
          ))}
        </div>

        <div className="mt-6">
          <h4 className={`font-semibold ${headingColor} mb-3`}>
            {content?.ContentFollowUp?.Heading}
          </h4>
          <ul className="space-y-2 text-gray-600">
            {content?.ContentFollowUp?.Content?.map((activity, index) => (
              <li key={index} className="flex items-start">
                <span className={`mr-3 ${headingColor} font-bold`}>
                  {index + 1}.
                </span>
                <span>{activity}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const NextStepsCard = ({
    content = {},
    bgColor = "bg-blue-50",
    headingColor = "text-gray-700",
  }) => {
    const stepContent = Array.isArray(content.Content) ? content.Content : [];

    return (
      <div className={`rounded-xl p-4 md:p-6 ${bgColor} shadow-lg w-full`}>
        <h2 className={`text-2xl font-bold ${headingColor} mb-4`}>
          {content?.RootHeading}
        </h2>

        <div className="space-y-4">
          {stepContent.length > 0 ? (
            stepContent.map((section, index) => (
              <div key={index} className="flex items-start">
                <span className={`mr-3 ${headingColor} font-bold text-lg`}>
                  {index + 1}.
                </span>
                <div>
                  <p className="text-gray-700 text-lg">
                    <span className={`font-semibold ${headingColor} mr-1`}>
                      {section.Heading || `Step ${index + 1}`}:
                    </span>
                    {section.Content || "No description available."}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 italic">No steps found.</p>
          )}
        </div>
      </div>
    );
  };

  const LoveStyleHighlight = ({ text }) => {
    if (!text) return null;
    const firstPart = text.split(".")[0];
    const remainingText = text.split(".").slice(1).join(".");
    return (
      <div className="bg-blue-100 p-4 rounded-lg">
        <p className="text-blue-800 text-sm">
          <span className="font-bold text-blue-800">{firstPart}.</span>
          {remainingText}
        </p>
      </div>
    );
  };

  function transformUserTraitsData(data) {
    if (!Array.isArray(data)) return { result: {}, totalScore: 0 };

    const result = data.reduce((acc, item) => {
      if (
        item?.trait?.title &&
        typeof item.percentage === "number" &&
        typeof item.score === "number"
      ) {
        acc[item.trait.title] = {
          percentage: item.percentage,
          score: item.score,
          updatedScore: item.score * 10,
        };
      }
      return acc;
    }, {});

    const totalScore = data.reduce((sum, item) => sum + (item?.score || 0), 0);

    return {
      result,
      totalScore,
      totalPercentage: (totalScore * 100) / 125,
    };
  }

  const updatedUserTraits = transformUserTraitsData(userTraits);

  const progressBarData = userTraits?.map((item, idx) => {
    const localeKey =
      traitTitleKeyMap[currentLocale] || traitTitleKeyMap[LOCALE.EN];

    return {
      label: snakeToTitleCase(item.trait[localeKey]),
      percentage: item?.percentage,
      color: progressBarColor[idx % progressBarColor.length],
    };
  });

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div>
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner title={contentData.header} color="blue" />
                  }
                >
                  {token && (
                    <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <h2 className="text-2xl font-semibold text-gray-700 mb-4">
                      {t("report.love_style.introduction")}
                    </h2>
                    <p className="text-gray-700 mb-6">{contentData?.intro}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start">
                      {/* Left Section: Text, Table & Explanation */}
                      <div>
                        {/* Table Section with better spacing */}
                        <h2 className="text-3xl font-semibold text-gray-700">
                          {yourLoveStyle?.RootHeading + " "}
                          <span className="font-normal">
                            {updatedUserTraits?.totalScore}/125
                          </span>
                        </h2>
                        <h2 className="text-2xl font-semibold text-gray-700 my-4">
                          {/* {updatedScoreTable?.FlavorText?.split(":")[0]} */}
                        </h2>
                        <ul className="space-y-4">
                          {progressBarData?.map((item, index) => {
                            return (
                              <ProgressBar
                                color={
                                  progressBarColor[
                                    index % progressBarColor.length
                                  ]
                                }
                                label={item?.label}
                                percentage={item?.percentage}
                              />
                            );
                          })}
                        </ul>
                      </div>

                      {/* Right Section: Image */}
                      <div className="flex items-center h-[90%] justify-center">
                        <img
                          src={result1Image}
                          alt="Banner-Image"
                          className="w-[95%] h-auto object-contain mt-1 sm:mt-6 md:mt-12"
                        />
                      </div>
                    </div>
                    <div className="my-8">
                      <LoveStyleHighlight
                        text={yourLoveStyle?.ContentFollowUp}
                      />
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={emotionalConnector?.RootHeading}
                      color="purple"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-[5fr_4fr] gap-8 md:gap-12 items-start">
                      {/* Left Section: Text, Table & Explanation */}
                      <LoveCard
                        content={emotionalConnector}
                        bgColor="bg-amber-50"
                        headingColor="text-amber-800"
                      />
                      {/* Right Section: Image */}
                      <div className="flex items-center justify-center rounded-xl h-full">
                        <img
                          src={result2Image}
                          alt="Banner-Image"
                          className="w-full h-full object-contain rounded-xl"
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={physicalAffectionateLover?.RootHeading}
                      color="red"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-[5fr_4fr] gap-8 md:gap-12 items-start">
                      {/* Left Section: Text, Table & Explanation */}

                      <LoveCard
                        content={physicalAffectionateLover}
                        bgColor="bg-blue-50"
                        headingColor="text-blue-800"
                      />

                      {/* Right Section: Image */}
                      <div className="flex items-center h-[100%] justify-center">
                        <img
                          src={result3Image}
                          alt="Banner-Image"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={practicalSupporter?.RootHeading}
                      color="green"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-[5fr_4fr] gap-8 md:gap-12 items-start">
                      {/* Left Section: Text, Table & Explanation */}

                      <LoveCard
                        content={practicalSupporter}
                        bgColor="bg-green-50"
                        headingColor="text-green-800"
                      />

                      {/* Right Section: Image */}
                      <div className="flex items-center h-[100%] justify-center">
                        <img
                          src={result4Image}
                          alt="Banner-Image"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={independentPartner?.RootHeading}
                      color="teal"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-[5fr_4fr] gap-8 md:gap-12 items-start">
                      {/* Left Section: Text, Table & Explanation */}

                      <LoveCard
                        content={independentPartner}
                        bgColor="bg-red-50"
                        headingColor="text-red-800"
                      />

                      {/* Right Section: Image */}
                      <div className="flex items-center h-[100%] justify-center">
                        <img
                          src={result5Image}
                          alt="Banner-Image"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={expressiveCommunicator?.RootHeading}
                      color="pink"
                    />
                  }
                >
                  <div className="w-full md:px-8 md:py-6 p-4">
                    <div className="grid grid-cols-1 md:grid-cols-[5fr_4fr] gap-8 md:gap-12 items-start">
                      {/* Left Section: Text, Table & Explanation */}

                      <LoveCard
                        content={expressiveCommunicator}
                        bgColor="bg-yellow-50"
                        headingColor="text-yellow-800"
                      />

                      {/* Right Section: Image */}
                      <div className="flex items-center h-[100%] justify-center">
                        <img
                          src={result6Image}
                          alt="Banner-Image"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                    <div className="mt-8">
                      <NextStepsCard content={nextSteps} />
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <CallToActionSection
                onClick1={() => {
                  navigate(
                    `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`
                  );
                }}
                onClick2={() => {
                  navigate(
                    `${quizPath.next}?assessmentId=${BIG_FIVE_TEST_ASSESSMENT_ID}&newt=true`
                  );
                }}
              />
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default LoveStyleReport;
